import { questions } from '../data/questions';

// Calculate step-by-step drop-off rates
export const calculateStepDropOffs = (allSubmissions) => {
  if (!allSubmissions || allSubmissions.length === 0) return {};

  // Separate completed and partial submissions
  const completedSubmissions = allSubmissions.filter(s => !s.isPartial);
  const partialSubmissions = allSubmissions.filter(s => s.isPartial);

  // Create question mapping for analysis
  const flatQuestions = [];
  const categoryMap = {};
  let questionIndex = 0;

  Object.entries(questions).forEach(([category, categoryQuestions]) => {
    categoryQuestions.forEach((question, idx) => {
      flatQuestions.push({
        ...question,
        category,
        categoryIndex: idx,
        globalIndex: questionIndex
      });
      categoryMap[questionIndex] = { category, index: idx };
      questionIndex++;
    });
  });

  // Initialise step counters
  const stepCounts = {
    landingPage: 0,
    startedAssessment: 0,
    reachedContact: 0,
    completed: completedSubmissions.length
  };

  // Count users at each major step
  stepCounts.landingPage = allSubmissions.length;
  stepCounts.startedAssessment = allSubmissions.length; // All submissions started
  stepCounts.reachedContact = completedSubmissions.length; // Only completed reached contact

  // Calculate question-level drop-offs
  const questionDropOffs = {};
  flatQuestions.forEach((q, index) => {
    const questionKey = `${q.category}-${q.categoryIndex}`;

    // Count how many users answered this question
    let answeredCount = 0;
    partialSubmissions.forEach(submission => {
      if (submission.responses?.raw?.[q.category]?.[q.categoryIndex] !== undefined) {
        answeredCount++;
      }
    });

    // Add completed submissions (they answered all questions)
    answeredCount += completedSubmissions.length;

    // Calculate drop-off rate for this question
    const totalStarted = allSubmissions.length;
    const completionRate = (answeredCount / totalStarted) * 100;
    const dropOffRate = 100 - completionRate;

    questionDropOffs[questionKey] = {
      questionText: q.text,
      category: q.category,
      globalIndex: index,
      answeredCount,
      totalStarted,
      completionRate: completionRate.toFixed(1),
      dropOffRate: dropOffRate.toFixed(1),
      questionNumber: index + 1
    };
  });

  // Calculate funnel conversion rates
  const funnelRates = {
    landingToStarted: stepCounts.landingPage > 0 ? 
      (stepCounts.startedAssessment / stepCounts.landingPage * 100).toFixed(1) : 0,
    startedToContact: stepCounts.startedAssessment > 0 ? 
      (stepCounts.reachedContact / stepCounts.startedAssessment * 100).toFixed(1) : 0,
    contactToComplete: stepCounts.reachedContact > 0 ? 
      (stepCounts.completed / stepCounts.reachedContact * 100).toFixed(1) : 0,
    overallConversion: stepCounts.landingPage > 0 ? 
      (stepCounts.completed / stepCounts.landingPage * 100).toFixed(1) : 0
  };

  return {
    stepCounts,
    questionDropOffs,
    funnelRates,
    totalQuestions: flatQuestions.length
  };
};

// Calculate question performance metrics
export const calculateQuestionPerformance = (allSubmissions) => {
  const dropOffData = calculateStepDropOffs(allSubmissions);
  if (!dropOffData.questionDropOffs) return [];

  // Convert to array and sort by drop-off rate (highest first)
  const questionPerformance = Object.values(dropOffData.questionDropOffs)
    .sort((a, b) => parseFloat(b.dropOffRate) - parseFloat(a.dropOffRate));

  return questionPerformance;
};

// Calculate critical drop-off points (top 5 worst performing questions)
export const calculateCriticalDropOffs = (allSubmissions) => {
  const questionPerformance = calculateQuestionPerformance(allSubmissions);
  return questionPerformance.slice(0, 5);
};

// Calculate category-level drop-off rates
export const calculateCategoryDropOffs = (allSubmissions) => {
  const questionPerformance = calculateQuestionPerformance(allSubmissions);
  const categoryDropOffs = {};

  // Group by category
  questionPerformance.forEach(q => {
    if (!categoryDropOffs[q.category]) {
      categoryDropOffs[q.category] = {
        category: q.category,
        questions: [],
        avgDropOffRate: 0,
        worstQuestion: null
      };
    }
    categoryDropOffs[q.category].questions.push(q);
  });

  // Calculate averages and find worst questions per category
  Object.keys(categoryDropOffs).forEach(category => {
    const questions = categoryDropOffs[category].questions;
    const avgDropOff = questions.reduce((sum, q) => sum + parseFloat(q.dropOffRate), 0) / questions.length;
    const worstQuestion = questions.reduce((worst, q) => 
      parseFloat(q.dropOffRate) > parseFloat(worst.dropOffRate) ? q : worst
    );

    categoryDropOffs[category].avgDropOffRate = avgDropOff.toFixed(1);
    categoryDropOffs[category].worstQuestion = worstQuestion;
  });

  return Object.values(categoryDropOffs);
};

// Calculate time-based patterns (mock data for now - would need timestamp tracking)
export const calculateTimePatterns = (allSubmissions) => {
  // This would require more detailed timestamp tracking in the future
  // For now, return basic patterns based on submission data
  const patterns = {
    averageTimeToComplete: '2.1 minutes', // Updated for shorter assessment
    quickDropOffs: '< 20 seconds', // Users who drop off immediately
    considerationDropOffs: '30-60 seconds', // Users who think then leave
    engagedDropOffs: '1+ minutes' // Users who engage but don't finish
  };

  return patterns;
};

// Calculate device/platform performance (mock data - would need user agent tracking)
export const calculateDevicePerformance = (allSubmissions) => {
  // This would require device tracking implementation
  // For now, return estimated data based on typical patterns
  return {
    mobile: {
      completionRate: '72%',
      dropOffRate: '28%',
      avgTimeSpent: '1.8 min'
    },
    desktop: {
      completionRate: '85%',
      dropOffRate: '15%',
      avgTimeSpent: '2.3 min'
    },
    tablet: {
      completionRate: '78%',
      dropOffRate: '22%',
      avgTimeSpent: '2.0 min'
    }
  };
};

// Generate actionable insights based on drop-off data
export const generateDropOffInsights = (allSubmissions) => {
  const criticalDropOffs = calculateCriticalDropOffs(allSubmissions);
  const categoryDropOffs = calculateCategoryDropOffs(allSubmissions);
  const funnelData = calculateStepDropOffs(allSubmissions);
  const insights = [];

  // Critical question insights
  if (criticalDropOffs.length > 0) {
    const worstQuestion = criticalDropOffs[0];
    insights.push({
      type: 'critical',
      title: 'Highest Drop-off Question',
      description: `Question ${worstQuestion.questionNumber} in ${worstQuestion.category} has a ${worstQuestion.dropOffRate}% drop-off rate`,
      recommendation: 'Consider simplifying this question or breaking it into multiple parts',
      priority: 'high'
    });
  }

  // Category insights
  const worstCategory = categoryDropOffs.sort((a, b) => 
    parseFloat(b.avgDropOffRate) - parseFloat(a.avgDropOffRate)
  )[0];

  if (worstCategory) {
    insights.push({
      type: 'category',
      title: 'Most Challenging Category',
      description: `${worstCategory.category} has an average ${worstCategory.avgDropOffRate}% drop-off rate`,
      recommendation: 'Review question wording and consider providing more context for this category',
      priority: 'medium'
    });
  }

  // Overall conversion insights
  const overallConversion = parseFloat(funnelData.funnelRates.overallConversion);
  if (overallConversion < 40) {
    insights.push({
      type: 'conversion',
      title: 'Low Overall Conversion',
      description: `Only ${overallConversion}% of visitors complete the assessment`,
      recommendation: 'Focus on reducing early drop-offs and improving question flow',
      priority: 'high'
    });
  }

  return insights;
};